import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type React from "react";
import type { Patient, ConversationSearchHit } from "@medassist/shared";
import { clearToken, getGroups } from "../lib/auth.js";
import { getNotifications } from "../lib/notifications.js";
import { revokeToken, isCognitoConfigured } from "../lib/cognito.js";
import { toggleTheme } from "../lib/theme.js";
import { patientTheme } from "../lib/patientTheme.js";
import { getPatients, addPatient, listNotificationEvents, countDeletedEvents, backfillRegimenRxcui, ApiError } from "../lib/api.js";
// The Dashboard is the default landing view — keep it in the initial chunk so the
// first paint never waits on a second network round-trip. Every other pane is
// route-split (React.lazy) so its code only downloads when the user opens it; this
// keeps the initial JS payload small (PERFORMANCE_PLAN.md #1). The panes are named
// exports, so each loader maps the module to a { default } for React.lazy.
import { DashboardPane } from "./DashboardPane.js";
import { Select } from "./Select.js";
const ChatPane = lazy(() => import("./ChatPane.js").then((m) => ({ default: m.ChatPane })));
const SearchPane = lazy(() => import("./SearchPane.js").then((m) => ({ default: m.SearchPane })));
const LogsPane = lazy(() => import("./LogsPane.js").then((m) => ({ default: m.LogsPane })));
const WaterPane = lazy(() => import("./WaterPane.js").then((m) => ({ default: m.WaterPane })));
const FoodPane = lazy(() => import("./FoodPane.js").then((m) => ({ default: m.FoodPane })));
const BowelPane = lazy(() => import("./BowelPane.js").then((m) => ({ default: m.BowelPane })));
const ActivityPane = lazy(() => import("./ActivityPane.js").then((m) => ({ default: m.ActivityPane })));
const MedicationsPane = lazy(() => import("./MedicationsPane.js").then((m) => ({ default: m.MedicationsPane })));
const PreferencesPane = lazy(() => import("./PreferencesPane.js").then((m) => ({ default: m.PreferencesPane })));
const AdminPane = lazy(() => import("./AdminPane.js").then((m) => ({ default: m.AdminPane })));
const HelpPane = lazy(() => import("./HelpPane.js").then((m) => ({ default: m.HelpPane })));
const AllergiesPane = lazy(() => import("./AllergiesPane.js").then((m) => ({ default: m.AllergiesPane })));
const FamilyHistoryPane = lazy(() => import("./FamilyHistoryPane.js").then((m) => ({ default: m.FamilyHistoryPane })));
const ChartsPane = lazy(() => import("./ChartsPane.js").then((m) => ({ default: m.ChartsPane })));
const BodyPane = lazy(() => import("./BodyPane.js").then((m) => ({ default: m.BodyPane })));
const TrashPane = lazy(() => import("./TrashPane.js").then((m) => ({ default: m.TrashPane })));
const AnalyzePane = lazy(() => import("./AnalyzePane.js").then((m) => ({ default: m.AnalyzePane })));
const ReportsPane = lazy(() => import("./ReportsPane.js").then((m) => ({ default: m.ReportsPane })));
const IntegrationsPane = lazy(() => import("./IntegrationsPane.js").then((m) => ({ default: m.IntegrationsPane })));
const DocIngestPane = lazy(() => import("./DocIngestPane.js").then((m) => ({ default: m.DocIngestPane })));
const ManageDataPane = lazy(() => import("./ManageDataPane.js").then((m) => ({ default: m.ManageDataPane })));
const MedicalHistoryPane = lazy(() => import("./MedicalHistoryPane.js").then((m) => ({ default: m.MedicalHistoryPane })));
// Injuries is no longer its own route (PAIN_TRACKING_PLAN.md Task 6.1, D-PAIN-9):
// InjuriesPane now renders inside PainPane's Injuries tab. The legacy "injuries"
// route is kept in VIEWS and redirects there (H8) so a saved view / bookmark
// never lands on a blank pane.
const PainPane = lazy(() => import("./PainPane.js").then((m) => ({ default: m.PainPane })));
const PhysicalTherapyPane = lazy(() => import("./PhysicalTherapyPane.js").then((m) => ({ default: m.PhysicalTherapyPane })));
const TrainerPane = lazy(() => import("./TrainerPane.js").then((m) => ({ default: m.TrainerPane })));
const ConditionsPane = lazy(() => import("./ConditionsPane.js").then((m) => ({ default: m.ConditionsPane })));
const UsersPane = lazy(() => import("./UsersPane.js").then((m) => ({ default: m.UsersPane })));
const DebugPanel = lazy(() => import("./DebugPanel.js").then((m) => ({ default: m.DebugPanel })));
import { DEBUG_VISIBLE, errorCount, subscribe } from "../lib/debug.js";
import { recordTiming } from "../lib/perf.js";
import { formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";
import { PageSkeleton } from "./Skeleton.js";
import { ConfirmDialog } from "./ModalShell.js";
import {
  MenuIcon, XIcon, ChevronDownIcon,
  ChatBubbleIcon, OnboardIcon, ClipboardListIcon, PillIcon, SettingsIcon,
  AlertTriangleIcon, UsersIcon, BarChartIcon, TrashIcon, UserPlusIcon,
  MagnifyingGlassIcon, DownloadIcon, PaperclipIcon, DatabaseIcon, MedHistoryIcon,
  FileTextIcon, DropletIcon, ActivityIcon, UtensilsIcon, SparklesIcon, DashboardIcon,
  CameraIcon, PoopIcon, BarbellIcon, LayersIcon, PhysioIcon, CrosshairIcon,
} from "./icons.js";
import { ClearableInput } from "./ClearableInput.js";

// Runtime list of all main views — single source of truth for both the View
// type and the URL-hash parser (so a stale/typo'd hash can be validated).
const VIEWS = ["dashboard", "chat", "onboard", "search", "logs", "water", "food", "bowel", "activity", "regimens", "preferences", "admin", "users", "allergies", "family-history", "medical-history", "injuries", "pain", "physical-therapy", "trainer", "conditions", "body", "charts", "reports", "trash", "help", "analyze", "integrations", "doc-ingest", "manage-data"] as const;
type View = (typeof VIEWS)[number];

// Browser tab title per view (ui-ux-standard rule 13). Section names only - never the active
// patient or any record - so no PHI reaches browser history, sync or screen shares. Typed as a
// Record so adding a view to VIEWS without a title here is a compile error.
const VIEW_TITLES: Record<View, string> = {
  dashboard: "Dashboard",
  chat: "Chat",
  onboard: "Onboard",
  search: "Search",
  logs: "Logs",
  water: "Log Water",
  food: "Log Food",
  bowel: "Log BM",
  activity: "Activity",
  regimens: "Medications",
  preferences: "Preferences",
  admin: "Admin",
  users: "Users",
  allergies: "Allergies",
  "family-history": "Family Hx",
  "medical-history": "Med History",
  injuries: "Pain", // legacy route - renders PainPane's Injuries tab
  pain: "Pain",
  "physical-therapy": "PT",
  trainer: "Trainer",
  conditions: "Conditions",
  body: "Body",
  charts: "Charts",
  reports: "Reports",
  trash: "Trash",
  help: "Help",
  analyze: "Analyze",
  integrations: "Integrations",
  "doc-ingest": "Doc OCR",
  "manage-data": "My Data",
};

// Navigation persistence (so a page refresh stays put — D-69 chrome).
// The current page lives in the URL hash (#/regimens) → refresh + browser
// back/forward both work. The active patient lives in localStorage (kept out
// of the URL so no patient identifier is ever exposed in the address bar).
const PATIENT_KEY = "medassist.activePatient";

function parseViewHash(isAdmin: boolean): View {
  const raw = location.hash.replace(/^#\/?/, "").split("?")[0].toLowerCase();
  if ((VIEWS as readonly string[]).includes(raw)) {
    const v = raw as View;
    if (v === "users" && !isAdmin) return "chat"; // role-gated view → fall back
    return v;
  }
  return "dashboard"; // default landing page (D-69)
}

function readStoredPatient(): string {
  try { return localStorage.getItem(PATIENT_KEY) ?? ""; } catch { return ""; }
}

function storePatient(id: string): void {
  try { localStorage.setItem(PATIENT_KEY, id); } catch { /* private mode / quota — non-fatal */ }
}

// Nav items render as real <a> anchors (href="#/<view>") so the browser treats
// them like normal page links: middle-click / Ctrl/Cmd-click open the target in
// a new tab. Plain left-clicks are intercepted for in-app SPA navigation; this
// predicate detects the modified/aux clicks we let fall through to the browser.
function isModifiedClick(e: React.MouseEvent): boolean {
  return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
}

// Single source of truth for main navigation (§31.3, D-69).
// Phase 3 (DASHBOARD_WIDGETS_PLAN.md): split into primary (always visible) and
// More (collapsible). Primary = Dashboard, Chat, Onboard, Logs, Search.
interface NavItem { label: string; view: View; Icon: React.ComponentType<{ size?: number; className?: string }> }

const PRIMARY_NAV_ITEMS: NavItem[] = [
  { label: "Dashboard",   view: "dashboard", Icon: DashboardIcon },
  { label: "Chat",        view: "chat",      Icon: ChatBubbleIcon },
  { label: "Onboard",    view: "onboard",   Icon: OnboardIcon },
  { label: "Logs",       view: "logs",      Icon: ClipboardListIcon },
  { label: "Search",     view: "search",    Icon: MagnifyingGlassIcon },
];

const MORE_NAV_ITEMS: NavItem[] = [
  { label: "Medications",  view: "regimens",          Icon: PillIcon },
  { label: "Trainer",      view: "trainer",           Icon: BarbellIcon },
  { label: "PT",           view: "physical-therapy",  Icon: PhysioIcon },
  { label: "Pain",         view: "pain",              Icon: CrosshairIcon },
  { label: "Log BM",       view: "bowel",             Icon: PoopIcon },
  { label: "Log Water",    view: "water",             Icon: DropletIcon },
  { label: "Log Food",     view: "food",              Icon: UtensilsIcon },
  { label: "Activity",     view: "activity",          Icon: ActivityIcon },
  { label: "Allergies",    view: "allergies",         Icon: AlertTriangleIcon },
  { label: "Family Hx",   view: "family-history",    Icon: UsersIcon },
  { label: "Med History",  view: "medical-history",   Icon: MedHistoryIcon },
  { label: "Conditions",   view: "conditions",        Icon: LayersIcon },
  { label: "Body",         view: "body",              Icon: CameraIcon },
  { label: "Analyze",      view: "analyze",           Icon: SparklesIcon },
  { label: "Charts",       view: "charts",            Icon: BarChartIcon },
  { label: "Reports",      view: "reports",           Icon: FileTextIcon },
  { label: "Integrations", view: "integrations",      Icon: DownloadIcon },
  { label: "Doc OCR",      view: "doc-ingest",        Icon: PaperclipIcon },
  { label: "My Data",      view: "manage-data",       Icon: DatabaseIcon },
  { label: "Trash",        view: "trash",             Icon: TrashIcon },
];

const MORE_OPEN_KEY = "medassist.moreNavOpen";

// Persistent app shell (PLAN.md §19.2, §31, D-69): responsive chrome.
// < md: phone top bar (hamburger + brand + patient) + slide-in drawer.
// md–lg: condensed horizontal nav in a wrapping header.
// lg+: persistent left sidebar, main pane beside it.
// Analyze mode is visible but disabled (Phase 2).
export function Shell({ onSignOut }: { onSignOut: () => void }) {
  const isAdmin = getGroups().includes("admin");
  const [patients, setPatients] = useState<Patient[]>([]);
  const [activeId, setActiveId] = useState<string>(() => readStoredPatient());
  const [view, setView] = useState<View>(() => parseViewHash(isAdmin));
  useDocumentTitle(formatPageTitle(VIEW_TITLES[view]));
  const [docIngestJobId, setDocIngestJobId] = useState<string | undefined>(undefined);
  // A pending "jump into a conversation" target set by a Search result click,
  // consumed once by the matching ChatPane (then cleared). (CONVERSATION_SEARCH_PLAN.md)
  const [chatJump, setChatJump] = useState<{ view: "chat" | "onboard"; sessionId: string; seq?: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [newPatientName, setNewPatientName] = useState("");
  const [addingPatient, setAddingPatient] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [errs, setErrs] = useState(errorCount());
  const [notifications, setNotifications] = useState<string[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [trashCount, setTrashCount] = useState(0);
  const [moreOpen, setMoreOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(MORE_OPEN_KEY) === "true"; } catch { return false; }
  });
  const [horizMoreOpen, setHorizMoreOpen] = useState(false);

  const drawerRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const horizMoreRef = useRef<HTMLDivElement>(null);
  // Wall-clock start of the most recent navigation, used to time how long a pane
  // takes to switch in (lazy-chunk fetch + render). Recorded per view (PERFORMANCE_PLAN.md).
  const navStartRef = useRef<number | null>(null);

  useEffect(() => { void load(); }, []);
  useEffect(() => subscribe(() => setErrs(errorCount())), []);

  // One-shot, idempotent backfill of canonical RxCUIs onto existing medications
  // (server-side, scoped to this user's patients). Fire-and-forget; the flag is
  // only set on success so a transient failure retries on the next app load.
  useEffect(() => {
    const KEY = "medassist.rxcuiBackfilled.v1";
    try { if (localStorage.getItem(KEY)) return; } catch { /* private mode — just run it */ }
    void backfillRegimenRxcui()
      .then(() => { try { localStorage.setItem(KEY, "1"); } catch { /* non-fatal */ } })
      .catch(() => { /* best-effort — new saves resolve on their own */ });
  }, []);

  // Close drawer on Escape key
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") closeDrawer(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // Focus the drawer panel when it opens (focus-trap entry point)
  useEffect(() => {
    if (drawerOpen) drawerRef.current?.focus();
  }, [drawerOpen]);

  function closeDrawer() {
    setDrawerOpen(false);
    hamburgerRef.current?.focus();
  }

  // Navigation writes the URL hash; the hashchange listener below mirrors it
  // back into `view`. Routing everything through the hash keeps one source of
  // truth and makes browser back/forward switch tabs.
  function navigate(v: View) {
    navStartRef.current = performance.now();
    const target = `#/${v}`;
    if (location.hash !== target) location.hash = target;
    else setView(v); // same hash → no hashchange fires; sync directly (no-op)
    if (drawerOpen) closeDrawer();
  }

  // Keep `view` in sync with the URL hash (refresh, back/forward, manual edit).
  useEffect(() => {
    function onHash() { setView(parseViewHash(isAdmin)); }
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [isAdmin]);

  // After each view change, record how long the switch took (mostly lazy-chunk
  // fetch + first render of the new pane). Skips the initial mount (no nav start).
  useEffect(() => {
    if (navStartRef.current == null) return;
    recordTiming(`view:${view}`, performance.now() - navStartRef.current);
    navStartRef.current = null;
  }, [view]);

  // Select a patient and remember it across refreshes.
  function selectPatient(id: string) {
    setActiveId(id);
    storePatient(id);
  }

  // A Search result was clicked: chat/onboarding hits jump into the conversation
  // at the matching message; event-note hits open the Logs view.
  function handleSearchJump(hit: ConversationSearchHit) {
    if (hit.source === "events") {
      navigate("logs");
      return;
    }
    const target = hit.source === "onboarding" ? "onboard" : "chat";
    if (hit.sessionId) setChatJump({ view: target, sessionId: hit.sessionId, seq: hit.seq });
    navigate(target);
  }

  async function load() {
    setLoading(true);
    try {
      const list = await getPatients();
      setPatients(list);
      // Reconcile the active patient once the list arrives: keep the
      // stored/current one if it still exists, else fall back to the first.
      if (list.length && !list.some((p) => p.patientId === activeId)) {
        selectPatient(list[0].patientId);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        clearToken();
        onSignOut();
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load patients");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!activeId) return;
    setDismissedNotifications(false);
    // Only fetch the few event types the reminder banner inspects — not the entire
    // timeline (PERFORMANCE_PLAN.md #2). This removes the redundant full-history fetch
    // that previously ran alongside the Dashboard's own load.
    void listNotificationEvents(activeId).then((evts) => setNotifications(getNotifications(evts))).catch(() => {});
  }, [activeId]);

  useEffect(() => {
    if (!activeId) return;
    void countDeletedEvents(activeId).then(setTrashCount).catch(() => {});
  }, [activeId]);

  // Auto-expand "More" when the active view lives there; close HorizNav dropdown on any navigation.
  useEffect(() => {
    setHorizMoreOpen(false);
    const isMoreView = MORE_NAV_ITEMS.some(i => i.view === view) || (isAdmin && view === "users");
    if (isMoreView) {
      setMoreOpen(true);
      try { localStorage.setItem(MORE_OPEN_KEY, "true"); } catch { /* non-fatal */ }
    }
  }, [view, isAdmin]);

  // Close the HorizNav "More" dropdown when clicking outside it.
  useEffect(() => {
    if (!horizMoreOpen) return;
    function onMouseDown(e: MouseEvent) {
      if (horizMoreRef.current && !horizMoreRef.current.contains(e.target as Node)) {
        setHorizMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [horizMoreOpen]);

  const active = patients.find((p) => p.patientId === activeId);

  function handleAdd() {
    setNewPatientName("");
    setAddPatientOpen(true);
  }

  async function submitAddPatient() {
    const name = newPatientName.trim();
    if (!name) return;
    setAddingPatient(true);
    try {
      const p = await addPatient(name);
      await load();
      selectPatient(p.patientId);
      setAddPatientOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add patient");
    } finally {
      setAddingPatient(false);
    }
  }

  async function handleSignOut() {
    if (isCognitoConfigured()) revokeToken();
    clearToken();
    onSignOut();
  }

  // ── Shared style constants ────────────────────────────────────────────────
  const navIdleCls =
    "rounded-md px-2 py-1 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800";
  const navActiveCls =
    "rounded-md px-2 py-1 text-sm bg-(--accent) text-(--accent-contrast)";
  const utilBtnCls =
    "rounded-md border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800";
  // Vertical nav (sidebar / drawer): full-width, 44 px touch target
  const vertNavIdleCls =
    "w-full rounded-md px-3 py-2.5 text-left text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 min-h-[44px]";
  const vertNavActiveCls =
    "w-full rounded-md px-3 py-2.5 text-left text-sm bg-(--accent) text-(--accent-contrast) min-h-[44px]";

  // ── Patient selector (phone drawer, tablet header, desktop sidebar) ───────
  const patientSelector = (
    <div className="flex items-center gap-2 min-w-0">
      {active && (
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          style={{ backgroundColor: active.color }}
        >
          {active.avatarInitial ?? active.firstName.charAt(0)}
        </span>
      )}
      <Select
        value={activeId}
        onChange={(e) => selectPatient(e.target.value)}
        disabled={!patients.length}
        className="min-w-0 flex-1"
      >
        {patients.map((p) => (
          <option key={p.patientId} value={p.patientId}>
            {p.firstName} {p.lastName ?? ""}
          </option>
        ))}
        {!patients.length && <option value="">No patients</option>}
      </Select>
      <button
        onClick={() => void handleAdd()}
        className="shrink-0 cursor-pointer rounded-md border border-gray-300 dark:border-gray-700 p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 inline-flex items-center justify-center max-sm:size-11 max-sm:p-0"
        title="Add patient"
        aria-label="Add patient"
      >
        <UserPlusIcon size={16} />
      </button>
      <a
        href="#/preferences"
        onClick={(e) => { if (isModifiedClick(e)) return; e.preventDefault(); navigate("preferences"); }}
        className={`shrink-0 cursor-pointer rounded-md border p-1.5 inline-flex items-center justify-center max-sm:size-11 max-sm:p-0 ${
          view === "preferences"
            ? "border-(--accent) bg-(--accent) text-(--accent-contrast)"
            : "border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        }`}
        title="Preferences"
        aria-label="Preferences"
      >
        <SettingsIcon size={16} />
      </a>
    </div>
  );

  // ── Utility buttons: Help | Debug | Theme | Sign out ─────────────────────
  // vertical=true: sidebar/drawer (full-width, 44 px touch target, no border)
  function UtilityButtons({ vertical }: { vertical?: boolean }) {
    const wrapCls = vertical ? "flex flex-col gap-0.5 p-2" : "flex items-center gap-2";
    const btnCls = vertical
      ? "w-full rounded-md px-3 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 min-h-[44px]"
      : utilBtnCls;
    const helpActiveCls = vertical
      ? "w-full rounded-md px-3 py-2.5 text-left text-sm bg-(--accent) text-(--accent-contrast) min-h-[44px]"
      : "rounded-md border px-2 py-1 text-sm border-(--accent) bg-(--accent) text-(--accent-contrast)";
    const helpIdleCls = vertical
      ? "w-full rounded-md px-3 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 min-h-[44px]"
      : utilBtnCls;
    return (
      <div className={wrapCls}>
        <a
          href="#/help"
          onClick={(e) => { if (isModifiedClick(e)) return; e.preventDefault(); navigate("help"); }}
          className={(vertical ? "flex items-center " : "") + (view === "help" ? helpActiveCls : helpIdleCls)}
          title="How to use this app"
        >
          Help
        </a>
        <a
          href="#/admin"
          onClick={(e) => { if (isModifiedClick(e)) return; e.preventDefault(); navigate("admin"); }}
          className={(vertical ? "flex items-center " : "") + (view === "admin" ? helpActiveCls : helpIdleCls)}
          title="Admin settings"
        >
          Admin
        </a>
        {DEBUG_VISIBLE && (
          <button
            onClick={() => setDebugOpen(true)}
            className={btnCls + (errs > 0 ? " text-red-600 dark:text-red-400" : "")}
            title="Open debug log"
          >
            Debug{errs > 0 ? ` (${errs})` : ""}
          </button>
        )}
        <button onClick={() => toggleTheme()} className={btnCls}>
          Theme
        </button>
        <button onClick={handleSignOut} className={btnCls}>
          Sign out
        </button>
      </div>
    );
  }

  // Primary nav is fixed; More nav appends Users for admins (D-DASH-W1).
  const primaryNavItems = PRIMARY_NAV_ITEMS;
  const moreNavItems = isAdmin
    ? [...MORE_NAV_ITEMS, { label: "Users", view: "users" as View, Icon: UserPlusIcon }]
    : MORE_NAV_ITEMS;

  // ── Horizontal nav (tablet header) ───────────────────────────────────────
  function HorizNav() {
    const isMoreActive = moreNavItems.some(i => i.view === view);
    return (
      <nav className="flex flex-wrap items-center gap-1" aria-label="Main navigation">
        {primaryNavItems.map((item) => (
          <a
            key={item.view}
            href={`#/${item.view}`}
            onClick={(e) => { if (isModifiedClick(e)) return; e.preventDefault(); navigate(item.view); }}
            className={view === item.view ? navActiveCls : navIdleCls}
          >
            <span className="flex items-center gap-1">
              <item.Icon size={14} />
              {item.label}
            </span>
          </a>
        ))}
        <div className="relative" ref={horizMoreRef}>
          <button
            onClick={() => setHorizMoreOpen(o => !o)}
            aria-expanded={horizMoreOpen}
            aria-haspopup="menu"
            className={"flex items-center gap-1 " + (isMoreActive || horizMoreOpen ? navActiveCls : navIdleCls)}
          >
            More
            <ChevronDownIcon size={12} className={"transition-transform duration-150" + (horizMoreOpen ? " rotate-180" : "")} />
          </button>
          {horizMoreOpen && (
            <div
              role="menu"
              className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg py-1"
            >
              {moreNavItems.map((item) => (
                <a
                  key={item.view}
                  href={`#/${item.view}`}
                  onClick={(e) => {
                    if (isModifiedClick(e)) return;
                    e.preventDefault();
                    setHorizMoreOpen(false);
                    navigate(item.view);
                  }}
                  className={"flex items-center gap-2.5 px-3 py-2 text-sm " + (view === item.view ? "bg-(--accent) text-(--accent-contrast)" : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800")}
                >
                  <item.Icon size={14} />
                  {item.label}
                  {item.view === "trash" && trashCount > 0 && (
                    <span className="ml-auto inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {trashCount}
                    </span>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>
    );
  }

  // ── Vertical nav (sidebar + drawer) ──────────────────────────────────────
  function VertNav({ onNavigate }: { onNavigate: (v: View) => void }) {
    function toggleMore() {
      const next = !moreOpen;
      setMoreOpen(next);
      try { localStorage.setItem(MORE_OPEN_KEY, String(next)); } catch { /* non-fatal */ }
    }
    return (
      <nav className="flex-1 overflow-y-auto p-2 space-y-0.5" aria-label="Main navigation">
        {primaryNavItems.map((item) => (
          <a
            key={item.view}
            href={`#/${item.view}`}
            onClick={(e) => { if (isModifiedClick(e)) return; e.preventDefault(); onNavigate(item.view); }}
            className={"flex items-center " + (view === item.view ? vertNavActiveCls : vertNavIdleCls)}
          >
            <span className="flex w-full items-center gap-2.5">
              <item.Icon size={15} />
              {item.label}
            </span>
          </a>
        ))}
        <div className="pt-1">
          <button
            onClick={toggleMore}
            aria-expanded={moreOpen}
            className="flex w-full items-center rounded-md px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 min-h-[36px]"
          >
            <ChevronDownIcon
              size={12}
              className={"mr-1.5 transition-transform duration-150" + (moreOpen ? " rotate-180" : "")}
            />
            More
          </button>
          {moreOpen && (
            <div className="mt-0.5 space-y-0.5">
              {moreNavItems.map((item) => (
                <a
                  key={item.view}
                  href={`#/${item.view}`}
                  onClick={(e) => { if (isModifiedClick(e)) return; e.preventDefault(); onNavigate(item.view); }}
                  className={"flex items-center " + (view === item.view ? vertNavActiveCls : vertNavIdleCls)}
                >
                  <span className="flex w-full items-center gap-2.5 justify-between">
                    <span className="flex items-center gap-2.5">
                      <item.Icon size={15} />
                      {item.label}
                    </span>
                    {item.view === "trash" && trashCount > 0 && (
                      <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white">
                        {trashCount}
                      </span>
                    )}
                  </span>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>
    );
  }

  // ── Banner zone (error + notifications) ──────────────────────────────────
  const banners = (
    <>
      {error && (
        <div className="bg-red-50 dark:bg-red-950 px-4 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {!dismissedNotifications && notifications.length > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-sm text-amber-800 dark:text-amber-300">
          <span className="flex-1">
            <strong>Follow-up reminders:</strong>{" "}
            {notifications.join(" · ")}
          </span>
          <button
            onClick={() => setDismissedNotifications(true)}
            className="shrink-0 text-amber-500 hover:text-amber-700 dark:hover:text-amber-200"
            aria-label="Dismiss notifications"
          >
            ✕
          </button>
        </div>
      )}
    </>
  );

  // ── Active pane ───────────────────────────────────────────────────────────
  const mainPane = loading ? (
    <PageSkeleton />
  ) : !active ? (
    <div className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400">
      <p>No patients yet.</p>
      <button
        onClick={() => void handleAdd()}
        className="mt-2 rounded-md bg-(--accent) px-3 py-1.5 text-(--accent-contrast) hover:bg-(--accent-hover)"
      >
        Add your first patient
      </button>
    </div>
  ) : view === "dashboard" ? (
    <DashboardPane
      key={active.patientId}
      patient={active}
      onNavigate={navigate}
      onPatientUpdated={(p) => setPatients((prev) => prev.map((x) => (x.patientId === p.patientId ? p : x)))}
    />
  ) : view === "help" ? (
    <HelpPane />
  ) : view === "chat" ? (
    <ChatPane
      key={active.patientId}
      patient={active}
      mode="capture"
      jumpTo={chatJump?.view === "chat" ? { sessionId: chatJump.sessionId, seq: chatJump.seq } : undefined}
      onJumpConsumed={() => setChatJump(null)}
      onDocUploaded={(jobId) => { setDocIngestJobId(jobId); navigate("doc-ingest"); }}
      onNavigate={(v) => navigate(v as View)}
    />
  ) : view === "onboard" ? (
    <ChatPane
      key={`onboard-${active.patientId}`}
      patient={active}
      mode="onboarding"
      jumpTo={chatJump?.view === "onboard" ? { sessionId: chatJump.sessionId, seq: chatJump.seq } : undefined}
      onJumpConsumed={() => setChatJump(null)}
      onConfigSaved={load}
    />
  ) : view === "search" ? (
    <SearchPane key={active.patientId} patient={active} onJump={handleSearchJump} />
  ) : view === "logs" ? (
    <LogsPane key={active.patientId} patient={active} />
  ) : view === "water" ? (
    <WaterPane
      key={active.patientId}
      patient={active}
      onPatientUpdated={(p) => setPatients((prev) => prev.map((x) => (x.patientId === p.patientId ? p : x)))}
    />
  ) : view === "food" ? (
    <FoodPane
      key={active.patientId}
      patient={active}
      onPatientUpdated={(p) => setPatients((prev) => prev.map((x) => (x.patientId === p.patientId ? p : x)))}
    />
  ) : view === "bowel" ? (
    <BowelPane
      key={active.patientId}
      patient={active}
      onPatientUpdated={(p) => setPatients((prev) => prev.map((x) => (x.patientId === p.patientId ? p : x)))}
    />
  ) : view === "activity" ? (
    <ActivityPane key={active.patientId} patient={active} />
  ) : view === "regimens" ? (
    <MedicationsPane key={active.patientId} patient={active} />
  ) : view === "admin" ? (
    <AdminPane />
  ) : view === "allergies" ? (
    <AllergiesPane key={active.patientId} patient={active} />
  ) : view === "family-history" ? (
    <FamilyHistoryPane key={active.patientId} patient={active} />
  ) : view === "medical-history" ? (
    <MedicalHistoryPane key={active.patientId} patient={active} />
  ) : view === "injuries" ? (
    // Legacy route → Pain page, Injuries tab preselected (H8, D-PAIN-9).
    <PainPane key={active.patientId} patient={active} initialTab="injuries" />
  ) : view === "physical-therapy" ? (
    <PhysicalTherapyPane key={active.patientId} patient={active} />
  ) : view === "pain" ? (
    <PainPane key={active.patientId} patient={active} />
  ) : view === "trainer" ? (
    <TrainerPane
      key={active.patientId}
      patient={active}
      onPatientUpdated={(p) => setPatients((prev) => prev.map((x) => (x.patientId === p.patientId ? p : x)))}
    />
  ) : view === "conditions" ? (
    <ConditionsPane key={active.patientId} patient={active} />
  ) : view === "body" ? (
    <BodyPane key={active.patientId} patient={active} />
  ) : view === "charts" ? (
    <ChartsPane key={active.patientId} patient={active} />
  ) : view === "reports" ? (
    <ReportsPane key={active.patientId} patient={active} />
  ) : view === "trash" ? (
    <TrashPane key={active.patientId} patient={active} />
  ) : view === "analyze" ? (
    <AnalyzePane key={active.patientId} patient={active} />
  ) : view === "integrations" ? (
    <IntegrationsPane key={active.patientId} patient={active} onPatientUpdated={(p) => { setPatients((prev) => prev.map((x) => x.patientId === p.patientId ? p : x)); }} onNavigateToDocIngest={() => navigate("doc-ingest")} />
  ) : view === "doc-ingest" ? (
    <DocIngestPane key={active.patientId} patient={active} initialJobId={docIngestJobId} />
  ) : view === "manage-data" ? (
    <ManageDataPane key={active.patientId} patient={active} patients={patients} />
  ) : view === "users" && isAdmin ? (
    <UsersPane />
  ) : (
    <PreferencesPane key={active.patientId} patient={active} onSaved={load} />
  );

  return (
    <div
      className="flex h-full flex-col bg-gray-50 dark:bg-gray-950 lg:flex-row"
      style={patientTheme(active?.color) as React.CSSProperties}
    >

      {/* ══════════════════════════════════════════════════════════════════
          PHONE TOP BAR  (< md)
          Hamburger | brand | avatar + patient selector
          ══════════════════════════════════════════════════════════════════ */}
      <header className="flex shrink-0 items-center gap-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 md:hidden">
        <button
          ref={hamburgerRef}
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
          aria-controls="nav-drawer"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <MenuIcon />
        </button>
        <span className="flex-1 truncate font-semibold text-gray-900 dark:text-gray-100">
          Medical Assistant
        </span>
        <div className="flex min-w-0 items-center gap-2">
          {active && (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
              style={{ backgroundColor: active.color }}
            >
              {active.avatarInitial ?? active.firstName.charAt(0)}
            </span>
          )}
          <Select
            value={activeId}
            onChange={(e) => selectPatient(e.target.value)}
            disabled={!patients.length}
            className="max-w-[130px] min-w-0"
          >
            {patients.map((p) => (
              <option key={p.patientId} value={p.patientId}>
                {p.firstName} {p.lastName ?? ""}
              </option>
            ))}
            {!patients.length && <option value="">No patients</option>}
          </Select>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════
          TABLET HEADER  (md–lg)
          Brand | patient | horizontal nav (wraps) | utilities
          ══════════════════════════════════════════════════════════════════ */}
      <header className="hidden shrink-0 flex-wrap items-center gap-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-2 md:flex lg:hidden">
        <span className="font-semibold text-gray-900 dark:text-gray-100">Medical Assistant</span>
        {patientSelector}
        <HorizNav />
        <div className="ml-auto">
          <UtilityButtons />
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP SIDEBAR  (lg+)
          Brand + patient at top, nav in middle, utilities pinned at bottom
          ══════════════════════════════════════════════════════════════════ */}
      <aside className="hidden lg:flex lg:w-56 lg:shrink-0 lg:flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="shrink-0 border-b border-gray-200 dark:border-gray-800 p-4 pb-3">
          <div className="mb-3 font-semibold text-gray-900 dark:text-gray-100">
            Medical Assistant
          </div>
          {patientSelector}
        </div>
        <VertNav onNavigate={navigate} />
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-800">
          <UtilityButtons vertical />
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════════════════════
          MAIN CONTENT  (all breakpoints)
          ══════════════════════════════════════════════════════════════════ */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {banners}
        <main className="min-h-0 flex-1">
          {/* Suspense covers the route-split panes: while a pane's chunk downloads,
              the same skeleton used for the initial data load is shown. */}
          <Suspense fallback={<PageSkeleton />}>
            {mainPane}
          </Suspense>
        </main>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          PHONE DRAWER  (fixed overlay, hidden lg+)
          Tap backdrop / Esc / ✕ to close.
          ══════════════════════════════════════════════════════════════════ */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="fixed inset-0 bg-black/40 motion-reduce:transition-none"
            aria-hidden="true"
            onClick={closeDrawer}
          />
          <div
            id="nav-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            tabIndex={-1}
            className="fixed bottom-0 left-0 top-0 flex w-72 flex-col bg-white dark:bg-gray-900 shadow-xl outline-none motion-reduce:transition-none"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4 py-3">
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                Medical Assistant
              </span>
              <button
                onClick={closeDrawer}
                aria-label="Close navigation"
                className="flex h-11 w-11 items-center justify-center rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <XIcon />
              </button>
            </div>
            <div className="shrink-0 border-b border-gray-200 dark:border-gray-800 p-3">
              {patientSelector}
            </div>
            <VertNav onNavigate={navigate} />
            <div className="shrink-0 border-t border-gray-200 dark:border-gray-800">
              <UtilityButtons vertical />
            </div>
          </div>
        </div>
      )}

      {DEBUG_VISIBLE && debugOpen && (
        <Suspense fallback={null}>
          <DebugPanel onClose={() => setDebugOpen(false)} />
        </Suspense>
      )}

      {addPatientOpen && (
        <ConfirmDialog
          title="Add patient"
          message={
            <ClearableInput
              autoFocus
              value={newPatientName}
              onChange={(e) => setNewPatientName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void submitAddPatient(); }}
              placeholder="First name"
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100"
            />
          }
          confirmLabel="Add"
          busy={addingPatient}
          onConfirm={() => void submitAddPatient()}
          onCancel={() => setAddPatientOpen(false)}
        />
      )}
    </div>
  );
}
